'use client';

import React, { MouseEvent } from 'react';
import { Box, Select, Option, Typography } from 'src/components';
import { CodeGroup } from '../CodeGroup';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faPlusCircle,
  faMinusCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { useEncounters } from 'src/context/providers/EncountersProvider';
import { EncountersActionTypes } from 'src/context/reducers/encountersReducer';
import { VolumeType } from 'src/context/reducers';

const BillingCodes = (): React.ReactElement => {
  const { state, dispatch } = useEncounters();
  const { billingCodes, volumeType } = state;

  const handleAddGroup = () => {
    dispatch({ type: EncountersActionTypes.ADD_CODE_GROUP });
    dispatch({
      type: EncountersActionTypes.SET_SHOW_ADD_GROUP,
      payload: { showAddGroup: true },
    });
  };

  const handleRemoveGroup = (groupId: string) => {
    dispatch({
      type: EncountersActionTypes.REMOVE_CODE_GROUP,
      payload: { groupId },
    });
    dispatch({
      type: EncountersActionTypes.SET_SHOW_ADD_GROUP,
      payload: { showAddGroup: false },
    });
  };

  const handleInterGroupOperatorChange = (
    _event: MouseEvent<Element> | null,
    value: string | null
  ) => {
    if (value && (value === 'AND' || value === 'OR')) {
      dispatch({
        type: EncountersActionTypes.SET_INTER_GROUP_OPERATOR,
        payload: { operator: value },
      });
    }
  };

  const canAddGroup =
    volumeType === VolumeType.ENCOUNTERS && billingCodes.groups.length < 2;

  const isTotalClaimsView = volumeType === VolumeType.TOTAL_CLAIMS;

  const billingCodesSubtext = isTotalClaimsView
    ? 'Combine procedure and diagnosis codes to build your search criteria.'
    : 'Combine procedure and diagnosis codes to build your Encounter criteria.';

  return (
    <Box className="relative flex flex-col justify-center items-center p-6 border-b border-neutral-300">
      <Box className="flex flex-col items-start w-full pb-6">
        <Typography className="text-primary font-bold text-lg">
          Billing Codes
        </Typography>
        <Typography className="text-tertiary text-sm">
          {billingCodesSubtext}
        </Typography>
      </Box>
      {billingCodes.groups.map((group, index) => (
        <React.Fragment key={group.id}>
          {index > 0 && (
            <Box className="relative w-full flex justify-center my-4">
              <div
                aria-hidden="true"
                className="absolute left-1/2 -translate-x-1/2 w-px bg-neutral-300"
                style={{ height: '1rem', bottom: 'calc(50% + 20px)' }}
              />
              <Select
                value={billingCodes.anyAllOperator}
                onChange={handleInterGroupOperatorChange}
                endDecorator={
                  <Icon
                    icon={faCaretDown}
                    className={`px-1 ${
                      isTotalClaimsView
                        ? 'text-neutral-400'
                        : 'text-neutral-600'
                    }`}
                    size="lg"
                  />
                }
                className={`w-24 px-0.5 h-10 !rounded-full text-base focus:border-sky-600 focus:border-1 focus:shadow-[0px_0px_2px_2px_theme(colors.sky.100)]  ${
                  isTotalClaimsView
                    ? 'bg-level-2 text-neutral-400'
                    : 'bg-white text-primary'
                }`}
                data-testid="encounters-drawer-billing-codes-and-or-select"
                disabled={isTotalClaimsView}
              >
                <Option value="AND">AND</Option>
                <Option value="OR">OR</Option>
              </Select>
              <div
                aria-hidden="true"
                className="absolute left-1/2 -translate-x-1/2 w-px bg-neutral-300"
                style={{ height: '1rem', top: 'calc(50% + 20px)' }}
              />
            </Box>
          )}
          <CodeGroup groupId={group.id} />

          {/* Add Remove Group button only for groups beyond the first one */}
          {index > 0 && (
            <Box className="flex flex-row justify-start w-full pt-6">
              <Box
                className="flex items-center cursor-pointer"
                onClick={() => handleRemoveGroup(group.id)}
                role="button"
                data-testid="encounters-drawer-billing-codes-remove-group-btn"
              >
                <Icon icon={faMinusCircle} className="text-sky-900 pr-1" />
                <Typography className="text-sky-900 font-bold text-sm">
                  Remove Group
                </Typography>
              </Box>
            </Box>
          )}
        </React.Fragment>
      ))}
      {canAddGroup && (
        <Box className="flex flex-row justify-start w-full pt-6">
          <Box
            className="flex items-center cursor-pointer"
            onClick={handleAddGroup}
            role="button"
            data-testid="encounters-drawer-billing-codes-add-another-group-btn"
          >
            <Icon icon={faPlusCircle} className="text-sky-900 pr-1" />
            <Typography className="text-sky-900 font-bold text-sm">
              Add Code Group
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default BillingCodes;
